/**
 * P12.4 — Project Review page
 * Route: /admin/project-review
 * Roles: super_admin, ops_manager, admin
 *
 * Lists every Job whose plan has not yet been approved by Ops/PM.
 *   • Shows system-derived plan + risk flags + scope photos.
 *   • Approve as-is → sets job.planningReview.status = "approved"
 *     (no archive entry created — approve-as-is is intentionally silent
 *      per P12.4 lock).
 *   • Override → typed CONFIRM dialog, appends to PlanningOverrideArchive,
 *     links archive entry id back to job.planningReview.archiveEntryId.
 *
 * Hard rules:
 *   • No contractValue exposure. We display payoutAmount (crew comp) only.
 *   • System plan never overwritten — overrides are overlays.
 */

import { useEffect, useMemo, useState } from "react";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { usePermissions } from "@/data/PermissionsContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { ClipboardList, MapPin, Users, Calendar, Shield } from "lucide-react";
import { formatAddress } from "@/lib/addressUtils";
import { getPortalSpec } from "@/data/portal/contractorPortalSelectors";
import {
  appendPlanningOverride,
  deriveSystemEstimate,
  getApprovedProjectPlan,
  getLatestOverrideForJob,
  logPlanningObservation,
} from "@/data/planningReview";
import type {
  PlanningOverrideReasonCategory,
  PlanningOverrideValues,
} from "@/data/planningReview/types";
import { ProjectReviewOverrideDialog } from "@/components/admin/ProjectReviewOverrideDialog";
import { useToast } from "@/hooks/use-toast";
import type { Job } from "@/data/types";

const ALLOWED_ROLES = new Set(["super_admin", "ops_manager", "admin", "it_staff"]);

export default function ProjectReview() {
  const { jobs, setJobs } = useAppData();
  const { role } = useRole();
  const { hasPermission: hasPerm } = usePermissions();
  const { toast } = useToast();
  const [overrideJob, setOverrideJob] = useState<Job | null>(null);

  const allowed = hasPerm("jobs_approve");

  const pending = useMemo(
    () => jobs.filter((j) => (j.planningReview?.status ?? "pending") === "pending"),
    [jobs],
  );
  const approved = useMemo(
    () => jobs.filter((j) => j.planningReview?.status === "approved"),
    [jobs],
  );

  // P12.5 — fire review_opened once per (role, allowed) snapshot.
  useEffect(() => {
    if (!allowed) return;
    if (pending.length === 0) return;
    logPlanningObservation("review_opened", "ALL_PENDING", role, {
      pendingCount: pending.length,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, role]);

  if (!allowed) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center">
        <Card>
          <CardContent className="p-6 space-y-2">
            <Shield className="h-8 w-8 text-warning mx-auto" />
            <p className="text-sm font-semibold">Restricted</p>
            <p className="text-xs text-muted-foreground">
              Project Review is available to Ops / PM / SuperAdmin only.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const approveAsIs = (job: Job) => {
    setJobs((prev) =>
      prev.map((j) =>
        j.id === job.id
          ? {
              ...j,
              planningReview: {
                status: "approved" as const,
                approvedBy: role,
                approvedAt: new Date().toISOString(),
              },
            }
          : j,
      ),
    );
    logPlanningObservation("approved_as_is", job.id, role);
    toast({
      title: "Approved",
      description: `${job.customerName} cleared for crew.`,
    });
  };

  const submitOverride = (
    job: Job,
    input: {
      override: PlanningOverrideValues;
      reasonCategory: PlanningOverrideReasonCategory;
      reasonNote: string;
    },
  ) => {
    const spec = getPortalSpec(job);
    const systemEstimate = deriveSystemEstimate(job, spec);
    const finalApproved = {
      days: input.override.days ?? systemEstimate.days,
      crewSize: input.override.crewSize ?? systemEstimate.crewSize,
      payoutAmount: input.override.payoutAmount ?? systemEstimate.payoutAmount,
      arrivalNote: input.override.arrivalNote,
    };
    const entry = appendPlanningOverride({
      jobId: job.id,
      createdBy: role,
      systemEstimate,
      override: input.override,
      finalApproved,
      reasonCategory: input.reasonCategory,
      reasonNote: input.reasonNote,
      inputsSnapshot: {
        projectType: job.projectType,
        permitRequired: job.permitRequired,
        scopeSummary: job.scopeSummary,
      },
      jobSpecSnapshot: {
        specId: spec?.specId,
        label: spec?.label,
        phaseCount: spec?.phases?.length ?? 0,
      },
    });
    if (!entry) {
      toast({
        title: "Override failed",
        description: "Could not record override.",
        variant: "destructive",
      });
      return;
    }
    setJobs((prev) =>
      prev.map((j) =>
        j.id === job.id
          ? {
              ...j,
              planningReview: {
                status: "approved" as const,
                approvedBy: role,
                approvedAt: new Date().toISOString(),
                archiveEntryId: entry.id,
              },
            }
          : j,
      ),
    );
    setOverrideJob(null);
    logPlanningObservation("override_applied", job.id, role, {
      reasonCategory: input.reasonCategory,
      daysDelta: finalApproved.days - systemEstimate.days,
      crewDelta: finalApproved.crewSize - systemEstimate.crewSize,
      payoutDelta: finalApproved.payoutAmount - systemEstimate.payoutAmount,
    });
    toast({
      title: "Override saved",
      description: `${job.customerName} cleared with overrides.`,
    });
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
      <header>
        <h1 className="text-xl font-bold flex items-center gap-2">
          <ClipboardList className="h-5 w-5 text-primary" />
          Project Review
        </h1>
        <p className="text-xs text-muted-foreground mt-0.5">
          Approve the system-derived install plan or override with a
          required reason. System estimates are never overwritten.
        </p>
      </header>

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" data-testid="pr-tab-pending">
            Pending ({pending.length})
          </TabsTrigger>
          <TabsTrigger value="approved" data-testid="pr-tab-approved">
            Approved ({approved.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-3 pt-3">
          {pending.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center text-xs text-muted-foreground">
                No jobs pending review.
              </CardContent>
            </Card>
          ) : (
            pending.map((job) => (
              <ReviewCard
                key={job.id}
                job={job}
                onApprove={() => approveAsIs(job)}
                onOverride={() => setOverrideJob(job)}
              />
            ))
          )}
        </TabsContent>

        <TabsContent value="approved" className="space-y-3 pt-3">
          {approved.slice(0, 50).map((job) => (
            <ReviewCard key={job.id} job={job} approved />
          ))}
        </TabsContent>
      </Tabs>

      {overrideJob && (
        <ProjectReviewOverrideDialog
          open={!!overrideJob}
          jobLabel={`${overrideJob.id} — ${overrideJob.customerName}`}
          systemPlan={deriveSystemEstimate(
            overrideJob,
            getPortalSpec(overrideJob),
          )}
          onCancel={() => setOverrideJob(null)}
          onConfirm={(input) => submitOverride(overrideJob, input)}
        />
      )}
    </div>
  );
}

function ReviewCard({
  job,
  approved,
  onApprove,
  onOverride,
}: {
  job: Job;
  approved?: boolean;
  onApprove?: () => void;
  onOverride?: () => void;
}) {
  const spec = getPortalSpec(job);
  const plan = getApprovedProjectPlan(job, spec);
  const archived = getLatestOverrideForJob(job.id);

  return (
    <Card data-testid="pr-job-card" data-job-id={job.id}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-mono text-muted-foreground">
              {job.id}
            </p>
            <h3 className="text-sm font-bold">{job.customerName}</h3>
            <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
              <MapPin className="h-3 w-3" />
              {formatAddress(job.address) || "Address TBD"}
            </p>
          </div>
          {approved ? (
            <Badge
              variant="outline"
              className="text-[10px] bg-success/10 text-success border-success/20"
            >
              Approved{plan.wasOverridden ? " (overridden)" : ""}
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="text-[10px] bg-warning/10 text-warning border-warning/20"
            >
              Pending review
            </Badge>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2 text-[11px]">
          <Stat
            icon={<Calendar className="h-3 w-3" />}
            label="Days"
            system={plan.systemPlan.days}
            final={plan.finalPlan.days}
          />
          <Stat
            icon={<Users className="h-3 w-3" />}
            label="Crew"
            system={plan.systemPlan.crewSize}
            final={plan.finalPlan.crewSize}
          />
          <Stat
            label="Payout"
            system={`$${plan.systemPlan.payoutAmount.toLocaleString()}`}
            final={`$${plan.finalPlan.payoutAmount.toLocaleString()}`}
          />
        </div>

        {plan.systemPlan.riskFlags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {plan.systemPlan.riskFlags.map((f) => (
              <Badge
                key={f}
                variant="outline"
                className="text-[10px] bg-warning/5 border-warning/20"
              >
                {f.replace(/_/g, " ")}
              </Badge>
            ))}
          </div>
        )}

        {archived && approved && (
          <p className="text-[10px] text-muted-foreground italic">
            Override reason: {archived.reasonCategory.replace(/_/g, " ")} —
            "{archived.reasonNote}"
          </p>
        )}

        {!approved && (
          <div className="flex gap-2 pt-1">
            <Button
              size="sm"
              variant="default"
              data-testid="pr-approve"
              onClick={onApprove}
            >
              Approve as-is
            </Button>
            <Button
              size="sm"
              variant="outline"
              data-testid="pr-override"
              onClick={onOverride}
            >
              Override…
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({
  icon,
  label,
  system,
  final,
}: {
  icon?: React.ReactNode;
  label: string;
  system: string | number;
  final: string | number;
}) {
  const changed = String(system) !== String(final);
  return (
    <div className="rounded-md border border-border/60 bg-card px-2 py-1.5">
      <p className="text-[9px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
        {icon}
        {label}
      </p>
      <p className="text-xs font-semibold mt-0.5">
        {final}
        {changed && (
          <span className="ml-1 text-[9px] text-muted-foreground line-through">
            {system}
          </span>
        )}
      </p>
    </div>
  );
}
